<template>
  <button
    :class="mergedClassName"
    :type="type"
    v-bind="$attrs"
    @click="handleClick"
  >
    <template v-if="loading">
      <div>{{ $t('g.loading') }}</div>
    </template>
    <template v-else>
      <span v-if="icon" class="shrink-0">
        <component :is="icon" />
      </span>
      <slot></slot>
    </template>
  </button>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted } from 'vue'

import { cn } from '@/utils/tailwindUtil'

type ButtonStyle =
  | 'primary'
  | 'secondary'
  | 'custom'
  | 'negative'
  | 'danger'
  | 'comfy'

const props = defineProps<{
  buttonStyle?: ButtonStyle
  icon?: unknown
  loading?: boolean
  enterKey?: boolean
  type?: 'reset' | 'submit' | 'button'
  className?: string
}>()

const emits = defineEmits<{
  (e: 'click', event: MouseEvent | KeyboardEvent): void
}>()

const buttonStyle = props.buttonStyle ?? 'primary'
const loading = props.loading ?? false
const enterKey = props.enterKey ?? false
const type = props.type ?? 'button'
const className = props.className ?? ''

const STYLE_MAP: Record<ButtonStyle, string> = {
  primary: 'bg-nordy-main text-nordy-main-bg hover:bg-nordy-main-hover',
  secondary: 'border-nordy-main border border-solid text-white hover:bg-[#333]',
  negative: 'bg-[#333] text-[#999] hover:bg-[#555]',
  danger: 'bg-red-500 font-bold text-white hover:bg-red-700',
  comfy: 'bg-comfy-500 hover:bg-comfy-700',
  custom: 'hover:bg-opacity-70'
}

const mergedClassName = computed(() =>
  cn(
    'transition-all hover:cursor-pointer disabled:cursor-not-allowed disabled:opacity-40',
    'rounded-lg',
    'center-box gap-[4px]',
    'tracking-[-0.32px]',
    STYLE_MAP[buttonStyle] ?? '',
    className
  )
)

const handleKeyDown = (e: KeyboardEvent) => {
  if (enterKey && e.key === 'Enter' && type !== 'submit') {
    emits('click', e)
  }
}

onMounted(() => {
  if (enterKey) {
    window.addEventListener('keydown', handleKeyDown)
  }
})
onBeforeUnmount(() => {
  if (enterKey) {
    window.removeEventListener('keydown', handleKeyDown)
  }
})

const handleClick = (e: MouseEvent) => {
  emits('click', e)
}
</script>
